import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Headphones,
  X,
  Phone,
  Mail,
  Gem,
  Map as MapIcon,
  PlaneTakeoff,
  PlaneLanding,
  Store,
  MapPinned,
  ImageOff,
  Tag as TagIcon,
  Sparkles,
} from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import AutoScroll from "embla-carousel-auto-scroll";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LANDING } from "@/constants/testIds";
import { fetchConfig, resolveMediaUrl } from "@/lib/ddfApi";
import { LANGUAGES, t, ensureTranslations } from "@/lib/i18n";
import { PromoThumb, ProductCard } from "@/components/landing/PromoCards";
import SiteHeader from "@/components/landing/SiteHeader";
import RegisterWidget from "@/components/landing/RegisterWidget";
import DrawSection from "@/components/landing/DrawSection";
import SocialBar from "@/components/landing/SocialBar";
import { useAdViewTracking, adKeyOf } from "@/lib/useAdViewTracking";
import AdProductsPanel from "@/components/landing/AdProductsPanel";
import { adProductIds } from "@/lib/adLinks";
import {
  trackEvent,
  trackPageView,
  trackDrawSectionView,
  bindSessionEnd,
} from "@/lib/analytics";

// Compute relative luminance and pick a 50% lighter/darker text color
// against the supplied hex background, whichever has better contrast.
function pickContrastingText(hex) {
  if (!hex || typeof hex !== "string") return "#ffffff";
  const m = hex.replace("#", "").match(/.{1,2}/g);
  if (!m || m.length < 3) return "#ffffff";
  const [r, g, b] = m.map((x) => parseInt(x, 16));
  // relative luminance per WCAG
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (lum > 0.5) {
    // bg is light → return 50% darker
    const dr = Math.max(0, Math.floor(r * 0.5));
    const dg = Math.max(0, Math.floor(g * 0.5));
    const db = Math.max(0, Math.floor(b * 0.5));
    return `#${[dr, dg, db].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }
  // bg is dark → return 50% lighter
  const lr = Math.min(255, Math.floor(r + (255 - r) * 0.5));
  const lg = Math.min(255, Math.floor(g + (255 - g) * 0.5));
  const lb = Math.min(255, Math.floor(b + (255 - b) * 0.5));
  return `#${[lr, lg, lb].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}
import AdminLoginDialog from "@/components/admin/AdminLoginDialog";
import AdminPanel from "@/components/admin/AdminPanel";
import SubscribeModal from "@/components/SubscribeModal";
import "@/styles/ddf.css";

const FALLBACK_BANNER =
  "https://customer-assets.emergentagent.com/job_22950d9a-7fcf-43fe-9449-0980ff587764/artifacts/kfujsa1p_special%20offer%20banner.jpg";
const FALLBACK_REGISTER =
  "https://customer-assets.emergentagent.com/job_22950d9a-7fcf-43fe-9449-0980ff587764/artifacts/i67261e9_register%20with%20us.png";
const FALLBACK_VERSACE =
  "https://customer-assets.emergentagent.com/job_22950d9a-7fcf-43fe-9449-0980ff587764/artifacts/6sqgclxl_banner%20ads.jpg";

// A promo is "live" (shown to visitors) when it is published, OR it is in the
// holding panel but its scheduled publish time has already passed. Legacy items
// without a status are treated as published. Additionally, any promo whose
// "Valid until" date has passed (end of that day, Asia/Dubai) is auto-removed.
function isPromoExpired(item) {
  if (!item?.validUntil) return false;
  const d = new Date(item.validUntil);
  if (Number.isNaN(d.getTime())) return false;
  // Expire at 23:59:59 of the valid-until date.
  d.setHours(23, 59, 59, 999);
  return new Date() > d;
}

function isPromoLive(item) {
  if (!item) return false;
  if (isPromoExpired(item)) return false;
  if (item.status === "holding") {
    if (item.publishAt) {
      const t = new Date(item.publishAt);
      return !Number.isNaN(t.getTime()) && t <= new Date();
    }
    return false;
  }
  return true;
}

// A banner ad is shown when live (no schedule) or its publishAt has passed, and
// its removeAt (auto-remove time) has NOT yet passed.
function isAdLive(ad) {
  if (!ad) return false;
  const now = new Date();
  if (ad.removeAt) {
    const r = new Date(ad.removeAt);
    if (!Number.isNaN(r.getTime()) && now >= r) return false;
  }
  if (ad.status === "holding") {
    if (ad.publishAt) {
      const p = new Date(ad.publishAt);
      return !Number.isNaN(p.getTime()) && p <= now;
    }
    return false;
  }
  return true;
}

export default function LandingPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("ddf_lang") || "en";
    return LANGUAGES.some((l) => l.code === stored) ? stored : "en";
  });
  const [, setMtVersion] = useState(0);
  const [activeLocation, setActiveLocation] = useState("departures");
  const [activeCategory, setActiveCategory] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [fullscreenImg, setFullscreenImg] = useState(null);
  const [welcomeMsg, setWelcomeMsg] = useState(null);
  const [promoApi, setPromoApi] = useState(null);
  const [adsApi, setAdsApi] = useState(null);
  const [adPanel, setAdPanel] = useState(null);
  const adsSectionRef = useRef(null);

  const mapsRef = useRef(null);
  const drawRef = useRef(null);
  const offersRef = useRef(null);
  const productGridRef = useRef(null);
  const registerRef = useRef(null);

  const autoplayPlugin = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  // Stable carousel options — avoid creating new object identities every render
  const promoCarouselOpts = useMemo(() => ({ align: "start", loop: false }), []);
  const adsCarouselOpts = useMemo(() => ({ align: "start", loop: true }), []);
  const mapsCarouselOpts = useMemo(() => ({ align: "start", loop: false }), []);
  const adsPlugins = useMemo(() => [autoplayPlugin.current], []);

  // Promo thumbnails: continuous auto-scroll marquee that visitors can also
  // grab-and-flick (dragFree) to slide through products faster.
  const promoAutoOpts = useMemo(
    () => ({ loop: true, dragFree: true, align: "start", containScroll: false }),
    []
  );
  const promoAutoScrollRef = useRef(
    AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const promoAutoPlugins = useMemo(() => [promoAutoScrollRef.current], []);

  // All values used inside come from stable imports (fetchConfig, resolveMediaUrl,
  // pickContrastingText) and stable React setters — no captured props/state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadConfig = useCallback(async () => {
    try {
      const data = await fetchConfig();
      setConfig(data);
      if (data?.styleSettings) {
        const root = document.documentElement;
        root.style.setProperty("--ddf-bg", data.styleSettings.bg || "#f5f2eb");
        root.style.setProperty("--ddf-text", data.styleSettings.textColor || "#1a1a1a");
        root.style.setProperty("--ddf-bar", data.styleSettings.barColor || "#c49a6c");
        root.style.setProperty("--ddf-deparr-bar", data.styleSettings.depArrBarColor || "#4a4a4a");
        root.style.setProperty("--ddf-shopmap-bar", data.styleSettings.shopMapBarColor || "#2c2c2c");
        root.style.setProperty("--ddf-draw-bar", data.styleSettings.drawBarColor || "#000000");
        root.style.setProperty("--ddf-deparr-text", data.styleSettings.depArrBarTextColor || "#ffffff");
        root.style.setProperty("--ddf-shopmap-text", data.styleSettings.shopMapBarTextColor || "#ffffff");
        root.style.setProperty("--ddf-draw-text", data.styleSettings.drawBarTextColor || "#ffffff");
        const catBg = data.styleSettings.categoryBgColor || "#c49a6c";
        root.style.setProperty("--ddf-category-bg", catBg);
        root.style.setProperty("--ddf-category-text", pickContrastingText(catBg));
        const bgImgRaw = data.styleSettings.bgImage || "";
        const bgImg = bgImgRaw ? resolveMediaUrl(bgImgRaw) : "";
        // Set CSS var; CSS rule on .ddf-root reads --ddf-bg-image
        root.style.setProperty("--ddf-bg-image", bgImg ? `url("${bgImg}")` : "none");
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to load config", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const pageViewFiredRef = useRef(false);
  const drawViewFiredRef = useRef(false);
  // Snapshot returning-visitor status at first render — BEFORE any effect runs
  // (and before trackPageView persists ddf_visitor_id). This avoids a false
  // "welcome back" flash on the very first visit under React StrictMode.
  const returningVisitorRef = useRef(
    !!localStorage.getItem("ddf_first_visit_done") ||
      !!localStorage.getItem("ddf_visitor_id")
  );

  useEffect(() => {
    // Returning-visitor greeting — use the first-render snapshot so StrictMode
    // double-invocation can't flash the toast on a genuine first visit.
    const hasVisited = returningVisitorRef.current;
    if (hasVisited && !sessionStorage.getItem("ddf_welcomed")) {
      sessionStorage.setItem("ddf_welcomed", "1");
      const subscribed = localStorage.getItem("ddf_subscribed") === "1";
      setWelcomeMsg(
        subscribed
          ? "Welcome Back to Dubai Duty Free, Valued Customer"
          : "Welcome Back to Dubai Duty Free"
      );
      setTimeout(() => setWelcomeMsg(null), 5000);
    }
    loadConfig();
    // Analytics: page-view fires exactly once even under React.StrictMode (dev)
    // double-invocation. bindSessionEnd is idempotent (guarded internally).
    if (!pageViewFiredRef.current) {
      pageViewFiredRef.current = true;
      trackPageView();
    }
    bindSessionEnd();
  }, [loadConfig]);

  // Re-init the promo auto-scroll carousel when its slides change (location
  // switch / config reload) so embla recomputes loop clones + bounds.
  useEffect(() => {
    if (promoApi) promoApi.reInit();
  }, [promoApi, activeLocation, config]);

  // Fire a single draw_section_view event when the user actually scrolls the
  // Exclusive Draw section into view (>=30% visible). Per-component-instance
  // ref guard ensures only one event per page session even under StrictMode.
  // drawRef is a stable ref; trackDrawSectionView is a module-level import —
  // no captured reactive values to list as deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const node = drawRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.3) {
            if (!drawViewFiredRef.current) {
              drawViewFiredRef.current = true;
              trackDrawSectionView();
            }
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: [0.3] }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // Apply RTL direction based on language
  useEffect(() => {
    const direction = LANGUAGES.find((l) => l.code === lang)?.dir || "ltr";
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("ddf_lang", lang);
    // Load live machine translations for the chosen language, then re-render.
    ensureTranslations(lang).then((changed) => {
      if (changed) setMtVersion((v) => v + 1);
    });
  }, [lang]);

  const promos = useMemo(() => {
    if (!config) return [];
    const list =
      activeLocation === "departures"
        ? config.departuresPromos || []
        : config.arrivalsPromos || [];
    return list.filter(isPromoLive);
  }, [config, activeLocation]);

  const categories = useMemo(() => {
    // Only include categories that have at least one promo item
    const counts = {};
    promos.forEach((p) => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return Object.keys(counts).filter((c) => c && counts[c] > 0);
  }, [promos]);

  const filteredPromos = useMemo(() => {
    if (!activeCategory) return promos;
    return promos.filter((p) => p.category === activeCategory);
  }, [promos, activeCategory]);

  const liveAds = useMemo(() => (config?.bannerAds || []).filter(isAdLive), [config]);
  useAdViewTracking(adsApi, liveAds, adsSectionRef);

  const handleAdminGearClick = () => {
    if (adminMode) setAdminMode(false);
    else setLoginOpen(true);
  };

  const scrollTo = (ref) => {
    setMobileMenuOpen(false);
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openProduct = (item) => {
    trackEvent("promo_card_click", {
      id: item.id,
      name: item.name,
      category: item.category,
      location: activeLocation,
    });
    // Image-only promos have no textual details — open the image full-screen.
    if (item.imageOnly) {
      const url =
        resolveMediaUrl(item.image) ||
        "https://placehold.co/600x600/eeeeee/333?text=Image";
      setFullscreenImg(url);
      return;
    }
    setActiveProduct(item);
    setProductOpen(true);
    trackEvent("popup_open", {
      id: item.id,
      name: item.name,
    });
  };

  const adLiveProducts = (ad) => {
    const all = [...(config.departuresPromos || []), ...(config.arrivalsPromos || [])];
    return adProductIds(ad)
      .map((id) => all.find((p) => p.id === id))
      .filter((p) => p && isPromoLive(p));
  };

  const onBannerAdClick = (ad, idx, items) => {
    trackEvent("banner_ad_click", { adId: adKeyOf(ad, idx), adIndex: idx, promoId: adProductIds(ad).join(","), linkUrl: ad.linkUrl || "" });
    if (items?.length) setAdPanel({ title: ad.name || "Featured Products", items });
  };

  // Selecting a promotion bar (Departures / Arrivals) switches location and
  // scrolls straight down to that location's product categories.
  const onSelectPromoLocation = (location) => {
    setActiveLocation(location);
    setActiveCategory(null);
    setTimeout(() => {
      productGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const openProductImageFullscreen = (url) => {
    setFullscreenImg(url);
  };

  const onPromoThumbClick = (location, item) => {
    setActiveLocation(location);
    setActiveCategory(item.category);
    trackEvent("promo_card_click", {
      id: item.id,
      name: item.name,
      location,
      source: "promo_thumb",
    });
    // scroll to the product grid (in-store promotion) section instead of opening popup
    setTimeout(() => {
      productGridRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 60);
  };

  const openRegister = (source = "header_nav") => {
    trackEvent("register_click", { source });
    if (config?.registerLink) {
      window.open(config.registerLink, "_blank", "noopener,noreferrer");
    }
  };

  // Scroll-only handler used by the top-nav and mobile-menu "Register" link.
  // Per requirement: nav Register must scroll to the on-page Register widget
  // instead of opening the external register URL directly.
  const scrollToRegister = (source = "nav_register_scroll") => {
    setMobileMenuOpen(false);
    trackEvent("register_nav_scroll", { source });
    registerRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading || !config) {
    return (
      <div className="ddf-loading" data-testid="loading">
        Loading Dubai Duty Free…
      </div>
    );
  }

  const bannerUrl = resolveMediaUrl(config.specialBanner?.url);
  const bannerType = config.specialBanner?.type || "image";
  const registerMediaUrl =
    resolveMediaUrl(config.registerMedia?.url) || FALLBACK_REGISTER;
  const millenniumImg =
    resolveMediaUrl(config.millenniumImg) ||
    "https://images.unsplash.com/photo-1561414927-6d86591d0c4f?w=800&q=80";
  const luxuryCarImg =
    resolveMediaUrl(config.luxuryCarImg) ||
    "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80";
  const motorcycleImg =
    resolveMediaUrl(config.motorcycleImg) ||
    "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800&q=80";
  const logoUrl = resolveMediaUrl(config.siteLogo?.url);

  const ss = config.styleSettings || {};
  const depBarStyle = {
    background: ss.depArrBarColor || "#4a4a4a",
    color: ss.depArrBarTextColor || "#ffffff",
  };
  const arrBarStyle = {
    background: ss.arrBarColor || "#4a4a4a",
    color: ss.arrBarTextColor || "#ffffff",
  };
  const locationHeaderStyle =
    activeLocation === "arrivals" ? arrBarStyle : depBarStyle;

  const navItems = [
    { key: "nav_offers", ref: offersRef, testId: LANDING.navMonthlyOffers, icon: TagIcon },
    { key: "nav_register", onClick: () => scrollToRegister("nav_register"), testId: LANDING.navRegister, icon: Mail },
    { key: "nav_draw", ref: drawRef, testId: LANDING.navDraw, icon: Gem },
    { key: "nav_maps", ref: mapsRef, testId: LANDING.navMaps, icon: MapIcon },
    { key: "nav_contact", onClick: () => { setMobileMenuOpen(false); setContactOpen(true); }, testId: "nav-contact-link", icon: Headphones },
  ];

  return (
    <div className="ddf-root" data-testid={LANDING.page}>
      <SubscribeModal config={config} />

      {welcomeMsg && (
        <div className="ddf-welcome-toast" role="status" data-testid="welcome-back-toast">
          <Sparkles size={18} className="ddf-welcome-icon" />
          <span className="ddf-welcome-text">{welcomeMsg}</span>
          <button
            type="button"
            className="ddf-welcome-close"
            onClick={() => setWelcomeMsg(null)}
            aria-label="Dismiss"
            data-testid="welcome-back-close"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <SiteHeader
        lang={lang}
        setLang={setLang}
        logoUrl={logoUrl}
        navItems={navItems}
        scrollTo={scrollTo}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        setContactOpen={setContactOpen}
      />

      {/* Special Offer Banner — edge-to-edge full width */}
      <section
        className="ddf-special-banner-edge"
        ref={offersRef}
        data-testid={LANDING.specialBanner}
      >
        <div className="ddf-special-banner">
          {(() => {
            if (!config.specialBanner?.url) {
              return (
                <div className="ddf-banner-empty">
                  <ImageOff size={64} strokeWidth={1.2} />
                  <p>No banner uploaded</p>
                </div>
              );
            }
            if (bannerType === "video") {
              return (
                <video
                  src={bannerUrl}
                  className="ddf-banner-video-locked"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls={false}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  preload="auto"
                  tabIndex={-1}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="special-banner-video"
                />
              );
            }
            return <img src={bannerUrl} alt="Special Offer" />;
          })()}
        </div>
      </section>

      {/* In-Store Promos — 2-tab layout with a draggable auto-scroll carousel */}
      <section className="ddf-promo-dual-edge ddf-promo-tabs-section">
        <div className="ddf-promo-tabs" role="tablist" aria-label="Promotion location">
          <button
            type="button"
            role="tab"
            aria-selected={activeLocation === "departures"}
            onClick={() => onSelectPromoLocation("departures")}
            style={depBarStyle}
            className={`ddf-promo-tab ${activeLocation === "departures" ? "is-active" : ""}`}
            data-testid="promo-tab-departures"
          >
            <PlaneTakeoff size={18} />
            <span>{t(lang, "departures_promo")}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeLocation === "arrivals"}
            onClick={() => onSelectPromoLocation("arrivals")}
            style={arrBarStyle}
            className={`ddf-promo-tab ${activeLocation === "arrivals" ? "is-active" : ""}`}
            data-testid="promo-tab-arrivals"
          >
            <PlaneLanding size={18} />
            <span>{t(lang, "arrivals_promo")}</span>
          </button>
        </div>

        {(() => {
          const source =
            activeLocation === "arrivals"
              ? config.arrivalsPromos || []
              : config.departuresPromos || [];
          const items = source.filter(isPromoLive);
          // Duplicate items so the row overflows and the loop stays seamless.
          const minCopies = items.length > 0 && items.length < 6 ? 4 : 2;
          const looped = Array.from({ length: minCopies }, () => items).flat();
          if (looped.length === 0) {
            return (
              <div className="ddf-promo-embla-empty" data-testid="promo-carousel-empty">
                {t(lang, "no_items")}
              </div>
            );
          }
          return (
            <Carousel
              opts={promoAutoOpts}
              plugins={promoAutoPlugins}
              setApi={setPromoApi}
              className="ddf-promo-embla"
              data-testid={
                activeLocation === "arrivals"
                  ? LANDING.arrivalsCarousel
                  : LANDING.departuresCarousel
              }
              aria-label={
                activeLocation === "arrivals"
                  ? t(lang, "arrivals_promo")
                  : t(lang, "departures_promo")
              }
            >
              <CarouselContent className="ddf-promo-embla-content">
                {looped.map((item, idx) => (
                  <CarouselItem
                    key={`${activeLocation}-${item.id || item.name}-${idx}`}
                    className="ddf-promo-embla-item"
                  >
                    <PromoThumb
                      item={item}
                      idx={idx}
                      location={activeLocation}
                      lang={lang}
                      active={activeCategory === item.category}
                      onClick={() => onPromoThumbClick(activeLocation, item)}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          );
        })()}
      </section>

      {/* Banner Ads Continuous Autoplay Loop — hidden when empty (moved AFTER promo thumbnails) */}
      {liveAds.length > 0 && (
        <section
          className="ddf-section ddf-banner-ads-section"
          data-testid={LANDING.bannerAdsLoop}
          ref={adsSectionRef}
        >
          <Carousel
            opts={adsCarouselOpts}
            plugins={adsPlugins}
            setApi={setAdsApi}
            className="ddf-ads-carousel"
          >
            <CarouselContent>
              {liveAds.map((ad, idx) => {
                const url = resolveMediaUrl(ad.url) || ad.url;
                const items = adLiveProducts(ad);
                const clickable = items.length > 0;
                const media =
                  ad.type === "video" ? (
                    <video src={url} autoPlay muted loop playsInline />
                  ) : (
                    <img src={url} alt={ad.name || `Ad ${idx + 1}`} />
                  );
                return (
                  <CarouselItem key={ad.url || `ad-${idx}`} className="basis-full">
                    {!clickable && ad.linkUrl ? (
                      <a
                        href={ad.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ddf-ad-slide ddf-ad-slide--clickable"
                        onClick={() => onBannerAdClick(ad, idx)}
                        data-testid={`banner-ad-${idx}`}
                      >
                        {media}
                      </a>
                    ) : (
                      <div
                        className={`ddf-ad-slide ${clickable ? "ddf-ad-slide--clickable" : ""}`}
                        onClick={clickable ? () => onBannerAdClick(ad, idx, items) : undefined}
                        role={clickable ? "button" : undefined}
                        data-testid={`banner-ad-${idx}`}
                      >
                        {media}
                      </div>
                    )}
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        </section>
      )}

      {/* Category Filter and Products - inside section separator */}
      <section className="ddf-section" ref={productGridRef}>
        <div className="ddf-section-separator">
          <h2
            className="ddf-section-title ddf-section-title--withicon"
            style={locationHeaderStyle}
            data-testid={LANDING.locationHeader}
          >
            <Store size={24} />
            {activeLocation === "departures"
              ? t(lang, "departures_promo")
              : t(lang, "arrivals_promo")}
          </h2>
          <div className="ddf-categories-row" data-testid={LANDING.categoryFilter}>
            <button
              className={`ddf-cat-btn ${!activeCategory ? "is-active" : ""}`}
              onClick={() => {
                setActiveCategory(null);
                trackEvent("category_tap", { category: "All" });
              }}
              data-testid={LANDING.categoryBtn}
            >
              {t(lang, "all")}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`ddf-cat-btn ${
                  activeCategory === cat ? "is-active" : ""
                }`}
                onClick={() => {
                  setActiveCategory(cat);
                  trackEvent("category_tap", { category: cat });
                }}
                data-testid={LANDING.categoryBtn}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="ddf-product-grid" data-testid={LANDING.productGrid}>
            {[...filteredPromos.filter((p) => !p.imageOnly), ...filteredPromos.filter((p) => p.imageOnly)].map((item, idx) => (
              <ProductCard
                key={item.id || idx}
                item={item}
                idx={idx}
                lang={lang}
                onClick={openProduct}
              />
            ))}
            {filteredPromos.length === 0 && (
              <div className="ddf-empty">{t(lang, "no_items")}</div>
            )}
          </div>
        </div>
      </section>

      {/* Shop Maps */}
      <section className="ddf-section" ref={mapsRef}>
        <h2 className="ddf-section-title ddf-section-title--centered ddf-section-title--edge">
          <MapPinned size={22} style={{ marginRight: 10 }} />
          {t(lang, "shop_map")}
        </h2>
        <Carousel
          opts={mapsCarouselOpts}
          className="ddf-shop-map-carousel"
          data-testid={LANDING.shopMapCarousel}
        >
          <CarouselContent>
            {((config.shopMaps || []).length === 0
              ? [
                  {
                    url:
                      "https://placehold.co/1200x500/cccccc/333?text=" +
                      encodeURIComponent(t(lang, "no_shop_map")),
                  },
                ]
              : config.shopMaps
            ).map((m, idx) => {
              const url = resolveMediaUrl(m.url) || m.url;
              return (
                <CarouselItem
                  key={m.url || `map-${idx}`}
                  className="basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <div
                    className="ddf-map-slide"
                    onClick={() => openProductImageFullscreen(url)}
                    role="button"
                  >
                    <img src={url} alt={`Map ${idx + 1}`} />
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </section>

      {/* Register Widget — entire image is clickable invisible button */}
      <RegisterWidget
        registerRef={registerRef}
        registerMediaUrl={registerMediaUrl}
        onRegister={openRegister}
        lang={lang}
      />

      {/* Exclusive Draw - matches SHOP MAP heading style with icon */}
      <DrawSection
        drawRef={drawRef}
        lang={lang}
        config={config}
        millenniumImg={millenniumImg}
        luxuryCarImg={luxuryCarImg}
        motorcycleImg={motorcycleImg}
      />

      {/* Social */}
      <SocialBar lang={lang} onAdminClick={handleAdminGearClick} />

      <footer className="ddf-footer" data-testid={LANDING.footer}>
        <p>© 2025 Dubai Duty Free</p>
        <p>Your premier duty-free shopping destination</p>
      </footer>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-sm" data-testid={LANDING.contactDialog}>
          <DialogHeader>
            <DialogTitle>{t(lang, "contact_title")}</DialogTitle>
            <DialogDescription>
              Dubai Duty Free Customer Service
            </DialogDescription>
          </DialogHeader>
          <div className="ddf-contact-rows">
            <div className="ddf-contact-row">
              <Phone size={18} />
              <div>
                <p className="ddf-contact-label">{t(lang, "contact_phone")}</p>
                <a href={`tel:${config.contactPhone}`}>{config.contactPhone}</a>
              </div>
            </div>
            <div className="ddf-contact-row">
              <Headphones size={18} />
              <div>
                <p className="ddf-contact-label">
                  {t(lang, "contact_tollfree")}
                </p>
                <span>{config.contactTollFree}</span>
              </div>
            </div>
            <div className="ddf-contact-row">
              <Mail size={18} />
              <div>
                <p className="ddf-contact-label">{t(lang, "contact_email")}</p>
                <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdProductsPanel
        panel={adPanel}
        onClose={() => setAdPanel(null)}
        onOpenProduct={openProduct}
        lang={lang}
      />

      {/* Product Detail Dialog */}
      <Dialog open={productOpen} onOpenChange={setProductOpen}>
        <DialogContent
          className="ddf-promo-modal"
          data-testid={LANDING.productDialog}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{activeProduct?.name || "Product"}</DialogTitle>
            <DialogDescription>
              {activeProduct?.description || activeProduct?.category || ""}
            </DialogDescription>
          </DialogHeader>
          {activeProduct && (
            <div className="ddf-promo-modal-card">
              <ProductCard item={activeProduct} idx={0} lang={lang} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Viewer for Shop Maps */}
      <Dialog open={!!fullscreenImg} onOpenChange={(v) => !v && setFullscreenImg(null)}>
        <DialogContent
          className="ddf-fullscreen-dialog"
          data-testid="fullscreen-image-dialog"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">Shop Map</DialogTitle>
          </DialogHeader>
          <button
            type="button"
            className="ddf-fullscreen-close"
            onClick={() => setFullscreenImg(null)}
            aria-label="Close map"
            data-testid="fullscreen-close-btn"
          >
            <X size={22} strokeWidth={2.75} />
          </button>
          {fullscreenImg && (
            <img
              src={fullscreenImg}
              alt="Shop Map full"
              className="ddf-fullscreen-img"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Login */}
      <AdminLoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onSuccess={() => {
          setLoginOpen(false);
          setAdminMode(true);
        }}
      />

      {/* Admin Panel */}
      {adminMode && (
        <AdminPanel
          config={config}
          onClose={() => setAdminMode(false)}
          onChanged={loadConfig}
        />
      )}
    </div>
  );
}
